package com.routexk.quiz.funnel.ui

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelPlan
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.ui.theme.toRgbString
import com.routexk.quiz.ui.theme.withAlpha
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import org.jetbrains.compose.web.css.*
import kotlin.math.max

fun formatUsdCents(cents: Long): String {
    val dollars = cents / 100
    val rem = (cents % 100).toInt()
    val remStr = if (rem < 10) "0$rem" else "$rem"
    return "$$dollars.$remStr"
}

/** Simple ascending bar chart in the hero comparison card. */
@Composable
fun MiniBarChart(
    heightsPct: List<Int>,
    modifier: Modifier = Modifier,
    barColorTop: com.varabyte.kobweb.compose.ui.graphics.Color = AppColors.Funnel.ChartGreenTop,
    barColorBottom: com.varabyte.kobweb.compose.ui.graphics.Color = AppColors.Funnel.ChartGreenBottom,
) {
    Row(
        modifier = modifier.height(110.rpx).gap(6.rpx),
        verticalAlignment = Alignment.Bottom
    ) {
        heightsPct.forEach { h ->
            Box(
                Modifier
                    .width(14.rpx)
                    .height(max(4, h).percent.unsafeCast<CSSLengthOrPercentageValue>())
                    .borderRadius(topLeft = 7.rpx, topRight = 7.rpx, bottomLeft = 7.rpx, bottomRight = 7.rpx)
                    .styleModifier {
                        property(
                            "background",
                            "linear-gradient(180deg, ${barColorTop.toRgbString()}, ${barColorBottom.toRgbString()})"
                        )
                    }
            )
        }
    }
}

@Composable
private fun ChartAxis(modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.height(110.rpx),
        verticalArrangement = Arrangement.SpaceBetween
    ) {
        listOf("\$4k", "\$2k", "\$0").forEach {
            SpanText(it, Modifier.fontSize(10.rpx).color(AppColors.Light.TextTertiary))
        }
    }
}

@Composable
private fun DollarChip(size: Int) {
    Box(
        Modifier
            .size(size.rpx)
            .borderRadius((size * 0.36).rpx)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xEE, 0xEE, 0xF4)),
        contentAlignment = Alignment.Center
    ) {
        org.jetbrains.compose.web.dom.Img(
            src = "/funnel/dollar-sign-icon.svg",
            attrs = {
                attr("aria-hidden", "true")
                style { width((size * 0.52).rpx); height((size * 0.52).rpx) }
            }
        )
    }
}

@Composable
private fun HandDrawnLabel(
    text: String,
    asset: String,
    // Bubble body size; the SVG canvas is larger because it bakes in the
    // drop-shadow bleed, so it is drawn oversized and offset to keep the
    // bubble itself at its design size and position.
    bodyWidth: Double, bodyHeight: Double,
    canvasWidth: Double, canvasHeight: Double,
    bleedLeft: Double, bleedTop: Double,
    textLift: Int,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier.width(bodyWidth.rpx).height(bodyHeight.rpx),
        contentAlignment = Alignment.Center
    ) {
        org.jetbrains.compose.web.dom.Img(
            src = asset,
            attrs = {
                attr("aria-hidden", "true")
                style {
                    position(Position.Absolute)
                    left((-bleedLeft).rpx)
                    top((-bleedTop).rpx)
                    width(canvasWidth.rpx)
                    height(canvasHeight.rpx)
                    property("max-width", "none")
                    property("pointer-events", "none")
                }
            }
        )
        SpanText(
            text,
            Modifier
                .fontSize(21.rpx)
                .fontWeight(FontWeight.SemiBold)
                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x1B, 0x1C, 0x31))
                .margin(bottom = textLift.rpx)
                .position(Position.Relative)
                .zIndex(1)
        )
    }
}

@Composable
private fun ChartRows(labels: List<String>, fontSize: Int) {
    // Distribute the gridlines across the full chart height so the bottom label
    // ($0) sits on the bar baseline — otherwise a fixed gap leaves the numbers
    // floating above the bars.
    Column(
        Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.SpaceBetween
    ) {
        labels.forEach { label ->
            Row(Modifier.fillMaxWidth().gap(6.rpx), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    label,
                    Modifier.fontSize(fontSize.rpx).fontWeight(300).color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x1B, 0x1C, 0x31))
                )
                Box(
                    Modifier
                        .fillMaxWidth()
                        .height(1.rpx)
                        .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xED, 0xED, 0xFA))
                )
            }
        }
    }
}

/** Hero "Now" vs "With RouteXK" comparison cards. */
@Composable
fun ComparisonCards(modifier: Modifier = Modifier) {
    val ink = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x1B, 0x1C, 0x31)

    Box(modifier = modifier.width(578.rpx).height(390.rpx).position(Position.Relative)) {
        // "Now" card — small, behind-left, flat gray chart
        Column(
            Modifier
                .position(Position.Absolute)
                .left(0.rpx)
                .top(105.rpx)
                .width(280.rpx)
                .backgroundColor(Colors.White)
                .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE9, 0xE9, 0xF0))
                .borderRadius(22.rpx)
                .padding(15.rpx)
                .gap(8.rpx)
                .styleModifier { property("box-shadow", "3.7px 3.7px 15px rgba(0,0,0,0.05)") }
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(9.rpx)) {
                DollarChip(24)
                SpanText(
                    FunnelCopy.HERO_CARD_TITLE,
                    Modifier.fontSize(15.rpx).fontWeight(FontWeight.Bold).color(ink)
                )
            }
            SpanText(FunnelCopy.HERO_NOW_VALUE, Modifier.fontSize(26.rpx).fontWeight(FontWeight.Bold).color(ink))
            Box(Modifier.fillMaxWidth().position(Position.Relative).height(120.rpx)) {
                ChartRows(listOf("\$4k", "\$2k", "\$0"), fontSize = 9)
                Row(
                    Modifier.position(Position.Absolute).bottom(8.rpx).left(30.rpx).gap(2.rpx),
                    verticalAlignment = Alignment.Bottom
                ) {
                    repeat(8) {
                        Box(
                            Modifier.width(22.rpx).height(3.rpx).borderRadius(7.rpx)
                                .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xC8, 0xC8, 0xE6))
                        )
                    }
                }
            }
        }

        // "With RouteXK" card — large, front-right, green border, rising chart
        Column(
            Modifier
                .position(Position.Absolute)
                .left(190.rpx)
                .top(39.rpx)
                .width(370.rpx)
                .backgroundColor(Colors.White)
                .border(1.6.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x2E, 0xBC, 0x6B))
                .borderRadius(28.rpx)
                .padding(20.rpx)
                .gap(10.rpx)
                .zIndex(2)
                .styleModifier { property("box-shadow", "-13px 9px 43px rgba(0,0,0,0.12)") }
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(10.rpx)) {
                DollarChip(32)
                SpanText(
                    FunnelCopy.HERO_CARD_TITLE,
                    Modifier.fontSize(20.rpx).fontWeight(FontWeight.Bold).color(ink)
                )
            }
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(10.rpx)) {
                SpanText(FunnelCopy.HERO_WITH_VALUE, Modifier.fontSize(35.rpx).fontWeight(FontWeight.Bold).color(ink))
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(4.rpx)) {
                    org.jetbrains.compose.web.dom.Img(
                        src = "/funnel/arrow-up-right-circle.svg",
                        attrs = { attr("aria-hidden", "true"); style { width(15.rpx); height(15.rpx) } }
                    )
                    SpanText(
                        FunnelCopy.HERO_WITH_BADGE,
                        Modifier.fontSize(15.rpx).fontWeight(FontWeight.Medium)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x29, 0x71, 0x29))
                    )
                }
            }
            Box(Modifier.fillMaxWidth().position(Position.Relative).height(190.rpx)) {
                ChartRows(listOf("\$4k", "\$2k", "\$0"), fontSize = 12)
                Row(
                    Modifier.position(Position.Absolute).bottom(6.rpx).left(36.rpx).gap(2.5.rpx),
                    verticalAlignment = Alignment.Bottom
                ) {
                    listOf(32, 43, 63, 120, 147, 111, 147, 182).forEach { h ->
                        Box(
                            Modifier
                                .width(32.rpx)
                                .height(h.rpx)
                                .borderRadius(topLeft = 10.rpx, topRight = 10.rpx, bottomLeft = 10.rpx, bottomRight = 10.rpx)
                                .styleModifier {
                                    property("background", "linear-gradient(180deg, #23A629 10%, #A1EFA5 100%)")
                                    property("box-shadow", "5px 5px 20px rgba(0,0,0,0.05)")
                                }
                        )
                    }
                }
            }
        }

        // Hand-drawn speech-bubble labels
        HandDrawnLabel(
            FunnelCopy.HERO_NOW_LABEL, "/funnel/label-now-arrow.svg",
            bodyWidth = 111.4, bodyHeight = 66.6,
            canvasWidth = 168.8, canvasHeight = 124.0,
            bleedLeft = 21.7, bleedTop = 21.7,
            textLift = 12,
            modifier = Modifier.position(Position.Absolute).left(48.rpx).top(47.rpx).zIndex(3)
        )
        HandDrawnLabel(
            FunnelCopy.HERO_WITH_LABEL, "/funnel/label-with-routexk-arrow.svg",
            bodyWidth = 182.9, bodyHeight = 61.3,
            canvasWidth = 220.4, canvasHeight = 98.8,
            bleedLeft = 14.1, bleedTop = 14.1,
            textLift = 9,
            modifier = Modifier.position(Position.Absolute).left(395.rpx).top(0.rpx).zIndex(3)
        )
    }
}

/** Initials avatar used in trust blocks and testimonials. */
@Composable
fun InitialsAvatar(name: String, size: Int = 36, modifier: Modifier = Modifier) {
    val initials = name.split(" ").mapNotNull { it.firstOrNull()?.toString() }.take(2).joinToString("")
    val palette = listOf("E5735E", "5E8DE5", "5EB98A", "B98AE5", "E5B25E")
    val bg = palette[(name.hashCode().let { if (it < 0) -it else it }) % palette.size]
    Box(
        modifier = modifier
            .size(size.rpx)
            .borderRadius(50.percent)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(bg.substring(0, 2).toInt(16), bg.substring(2, 4).toInt(16), bg.substring(4, 6).toInt(16)))
            .border(2.rpx, LineStyle.Solid, Colors.White),
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            initials,
            Modifier.fontSize((size * 0.38).rpx).fontWeight(FontWeight.Bold).color(Colors.White)
        )
    }
}

@Composable
fun TrustBlock(modifier: Modifier = Modifier) {
    Column(
        modifier = modifier.gap(8.rpx),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        org.jetbrains.compose.web.dom.Img(
            src = "/funnel/trustpilot.svg",
            attrs = {
                attr("alt", "Trustpilot")
                style { width(130.rpx); height(32.rpx) }
            }
        )
        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(12.rpx)) {
            Row {
                listOf("avatar1", "avatar2", "avatar3").forEachIndexed { i, asset ->
                    org.jetbrains.compose.web.dom.Img(
                        src = "/funnel/$asset.png",
                        attrs = {
                            attr("alt", "")
                            style {
                                width(36.rpx); height(36.rpx)
                                borderRadius(50.percent)
                                if (i > 0) marginLeft((-15).rpx)
                                property("border", "2px solid white")
                                property("position", "relative")
                            }
                        }
                    )
                }
            }
            org.jetbrains.compose.web.dom.P({
                style {
                    fontSize(18.rpx)
                    lineHeight("24px")
                    margin(0.rpx)
                    color(Color("#6B6B6B"))
                }
            }) {
                org.jetbrains.compose.web.dom.Text("Trusted by ")
                org.jetbrains.compose.web.dom.Span({
                    style { fontWeight(600); color(Color("#12181C")) }
                }) { org.jetbrains.compose.web.dom.Text("1,211,650+") }
                org.jetbrains.compose.web.dom.Text(" drivers")
            }
        }
    }
}

/** Donut progress ring for the "AI analysis" screen (red arc on #E6E6EF track). */
@Composable
fun AnalysisRing(percent: Int, modifier: Modifier = Modifier) {
    // Progress ring 0..100%: light track, round-capped red arc that fills up.
    // The arc is an inline SVG whose dashoffset is updated imperatively, with
    // a CSS transition smoothing the movement between counter ticks.
    val circumference = 458.67 // 2 * PI * r(73)
    var arc by remember { mutableStateOf<org.w3c.dom.Element?>(null) }
    Box(
        modifier = modifier
            .size(160.rpx)
            .position(Position.Relative)
            .attrsModifier {
                attr("role", "progressbar")
                attr("aria-valuenow", percent.toString())
                attr("aria-valuemin", "0")
                attr("aria-valuemax", "100")
            },
        contentAlignment = Alignment.Center
    ) {
        Box(
            Modifier
                .fillMaxSize()
                .borderRadius(50.percent)
                .border(14.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE9, 0xE9, 0xF0))
        )
        org.jetbrains.compose.web.dom.Div(
            attrs = {
                style {
                    position(Position.Absolute)
                    property("inset", "0")
                    property("pointer-events", "none")
                }
                ref { el ->
                    el.innerHTML =
                        "<svg width=\"100%\" height=\"100%\" viewBox=\"0 0 160 160\" style=\"display:block\">" +
                            "<circle cx=\"80\" cy=\"80\" r=\"73\" fill=\"none\" stroke=\"#ED272B\" " +
                            "stroke-width=\"14\" stroke-linecap=\"round\" " +
                            "stroke-dasharray=\"$circumference\" stroke-dashoffset=\"$circumference\" " +
                            "transform=\"rotate(-90 80 80)\" " +
                            "style=\"transition: stroke-dashoffset 150ms linear\"/>" +
                            "</svg>"
                    arc = el.querySelector("circle")
                    onDispose { arc = null }
                }
            }
        )
        SideEffect {
            arc?.setAttribute("stroke-dashoffset", ((1 - percent / 100.0) * circumference).toString())
        }
        SpanText(
            "$percent%",
            Modifier.fontSize(24.rpx).fontWeight(FontWeight.Bold)
                .color(AppColors.Funnel.TextDark)
        )
    }
}

@Composable
fun GoldStars(count: Int = 5, size: Int = 14, modifier: Modifier = Modifier) {
    SpanText(
        "★".repeat(count),
        modifier
            .fontSize(size.rpx)
            .letterSpacing(2.rpx)
            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF6, 0xBE, 0x4F))
    )
}

private val TESTIMONIAL_AVATARS = mapOf(
    "Jake M." to "/funnel/testimonial-1.png",
    "Carlos R." to "/funnel/testimonial-3.png",
    "James R." to "/funnel/testimonial-4.png",
    "Laura S." to "/funnel/testimonial-5.png",
    "Omar J." to "/funnel/testimonial-6.png",
)

@Composable
fun TestimonialCard(
    testimonial: FunnelCopy.Testimonial,
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .backgroundColor(Colors.White)
            .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xEB, 0xEB, 0xF3))
            .borderRadius(20.rpx)
            .padding(20.rpx)
            .styleModifier { property("box-shadow", "4px 4px 8px rgba(0,0,0,0.05)") }
    ) {
        Column(Modifier.fillMaxWidth().gap(14.rpx)) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(14.rpx)) {
                val avatar = TESTIMONIAL_AVATARS[testimonial.name]
                if (avatar != null) {
                    org.jetbrains.compose.web.dom.Img(
                        src = avatar,
                        attrs = {
                            attr("alt", "")
                            style { width(44.rpx); height(44.rpx); borderRadius(50.percent) }
                        }
                    )
                } else {
                    InitialsAvatar(testimonial.name, size = 44)
                }
                Column(Modifier.gap(4.rpx)) {
                    SpanText(
                        testimonial.name,
                        Modifier.fontSize(16.rpx).fontWeight(FontWeight.Bold)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x12, 0x18, 0x1C))
                    )
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(8.rpx)) {
                        GoldStars()
                        SpanText(
                            testimonial.timeAgo,
                            Modifier.fontSize(14.rpx)
                                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x5E, 0x5E, 0x5E))
                        )
                    }
                }
            }
            SpanText(
                testimonial.text,
                Modifier.fontSize(16.rpx).fontWeight(FontWeight.Medium)
                    .lineHeight(22.rpx)
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x40, 0x40, 0x40))
            )
        }
    }
}

/** Results-page blue card: superscript $ + huge amount + divider + glass pill. */
@Composable
fun DeductionCard(
    title: String,
    amountDigits: String,
    pill: String,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .maxWidth(450.rpx)
            .fillMaxWidth()
            .moneyGradient()
            .borderRadius(24.rpx)
            .padding(24.rpx)
            .gap(16.rpx)
    ) {
        SpanText(
            title,
            Modifier
                .fontSize(16.rpx)
                .fontWeight(FontWeight.SemiBold)
                .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                .color(Colors.White)
        )
        Row(verticalAlignment = Alignment.Top) {
            SpanText(
                "$",
                Modifier
                    .fontSize(24.rpx)
                    .lineHeight(64.rpx)
                    .fontWeight(FontWeight.Bold)
                    .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                    .color(Colors.White)
            )
            SpanText(
                amountDigits,
                Modifier
                    .fontSize(76.rpx)
                    .lineHeight(80.rpx)
                    .fontWeight(FontWeight.Bold)
                    .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                    .color(Colors.White)
            )
        }
        Box(Modifier.fillMaxWidth().height(1.rpx).backgroundColor(Colors.White.withAlpha(0.2f)))
        Row(
            Modifier
                .fillMaxWidth()
                .backgroundColor(Colors.White.withAlpha(0.2f))
                .border(1.rpx, LineStyle.Solid, Colors.White.withAlpha(0.3f))
                .borderRadius(100.rpx)
                .padding(topBottom = 12.rpx, leftRight = 20.rpx)
                .gap(12.rpx),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(Modifier.size(6.rpx).flexShrink(0).borderRadius(50.percent).backgroundColor(Colors.White))
            SpanText(
                pill,
                Modifier
                    .fontSize(14.rpx)
                    .lineHeight(20.rpx)
                    .fontFamily(AppTypography.TERTIARY_FONT_NAME, AppTypography.TERTIARY_FONT_FALLBACK)
                    .color(Colors.White)
            )
        }
    }
}

/** Blue gradient money stat card. */
@Composable
fun MoneyCard(
    title: String,
    value: String,
    pill: String?,
    modifier: Modifier = Modifier,
    note: String? = null
) {
    Column(
        modifier = modifier
            .moneyGradient()
            .borderRadius(24.rpx)
            .padding(topBottom = 24.rpx, leftRight = 20.rpx)
            .gap(12.rpx)
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.gap(8.rpx).margin(bottom = 12.rpx)
        ) {
            org.jetbrains.compose.web.dom.Img(
                src = "/funnel/dollar-icon-white.svg",
                attrs = { attr("aria-hidden", "true"); style { width(24.rpx); height(24.rpx) } }
            )
            SpanText(
                title,
                Modifier.fontSize(16.rpx).lineHeight(18.rpx).fontWeight(FontWeight.Medium).color(Colors.White)
            )
        }
        SpanText(
            value,
            Modifier.fontSize(48.rpx).lineHeight(60.rpx).fontWeight(FontWeight.Bold).color(Colors.White)
        )
        pill?.let {
            Row(
                Modifier
                    .backgroundColor(Colors.White.withAlpha(0.2f))
                    .border(1.rpx, LineStyle.Solid, Colors.White.withAlpha(0.3f))
                    .borderRadius(100.rpx)
                    .padding(topBottom = 8.rpx, leftRight = 12.rpx)
                    .gap(8.rpx),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(Modifier.size(6.rpx).flexShrink(0).borderRadius(50.percent).backgroundColor(Colors.White))
                SpanText(
                    it,
                    Modifier.fontSize(12.rpx).lineHeight(14.rpx).fontWeight(FontWeight.SemiBold).color(Colors.White)
                )
            }
        }
        note?.let {
            SpanText(
                it,
                Modifier.fontSize(14.rpx).lineHeight(16.rpx).color(Colors.White.withAlpha(0.8f))
            )
        }
    }
}

@Composable
fun WhiteStatCard(
    title: String,
    icon: String,
    value: String,
    unit: String,
    pill: String,
    modifier: Modifier = Modifier
) {
    val slate = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B)
    Box(
        modifier = modifier
            .backgroundColor(Colors.White)
            .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE7, 0xE7, 0xED))
            .borderRadius(24.rpx)
            .padding(topBottom = 24.rpx, leftRight = 20.rpx)
            .styleModifier { property("box-shadow", "12px 12px 12px rgba(0, 66, 139, 0.08)") }
    ) {
        Column(Modifier.fillMaxWidth().gap(12.rpx)) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.gap(8.rpx).margin(bottom = 24.rpx)
            ) {
                org.jetbrains.compose.web.dom.Img(
                    src = icon,
                    attrs = { attr("aria-hidden", "true"); style { width(24.rpx); height(24.rpx) } }
                )
                SpanText(title, Modifier.fontSize(16.rpx).lineHeight(18.rpx).fontWeight(FontWeight.Medium).color(slate))
            }
            Row(verticalAlignment = Alignment.Bottom, modifier = Modifier.gap(4.rpx)) {
                SpanText(
                    value,
                    Modifier.fontSize(40.rpx).lineHeight(44.rpx).fontWeight(FontWeight.Bold)
                        .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x01, 0x02, 0x26))
                )
                SpanText(unit, Modifier.fontSize(18.rpx).lineHeight(28.rpx).color(slate))
            }
            Row(
                Modifier
                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF9, 0xF9, 0xFB))
                    .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xEC, 0xEC, 0xF3))
                    .borderRadius(14.rpx)
                    .padding(topBottom = 8.rpx, leftRight = 12.rpx)
                    .gap(6.rpx),
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText(pill, Modifier.fontSize(12.rpx).lineHeight(14.rpx).color(slate))
            }
        }
    }
}

data class PotentialItem(val label: String, val level: String, val percent: Int, val icon: String)

private fun levelColors(level: String): Pair<String, String> = when {
    level.contains("EXTREMELY") -> "#49A43C" to "#FFFFFF"
    level == "MEDIUM" -> "#DAF64F" to "#010101"
    else -> "#AFDB5E" to "#010101"
}

@Composable
fun PotentialBars(items: List<PotentialItem>, modifier: Modifier = Modifier, compact: Boolean = false) {
    FunnelCard(modifier = modifier, padding = if (compact) 14.rpx else 26.rpx) {
        Column(Modifier.fillMaxWidth().gap(if (compact) 12.rpx else 28.rpx)) {
            items.forEach { item ->
                val (badgeBg, badgeFg) = levelColors(item.level)
                Column(Modifier.fillMaxWidth().gap(if (compact) 8.rpx else 12.rpx)) {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(8.rpx)) {
                            org.jetbrains.compose.web.dom.Img(
                                src = item.icon,
                                attrs = { attr("aria-hidden", "true"); style { width(18.rpx); height(18.rpx) } }
                            )
                            SpanText(
                                item.label,
                                Modifier.fontSize(if (compact) 15.rpx else 16.rpx).fontWeight(FontWeight.Medium)
                                    .whiteSpace(com.varabyte.kobweb.compose.css.WhiteSpace.NoWrap)
                                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                            )
                            SpanText(
                                item.level,
                                Modifier
                                    .fontSize(if (compact) 10.rpx else 12.rpx)
                                    .fontWeight(FontWeight.Bold)
                                    .letterSpacing(0.5.rpx)
                                    .whiteSpace(com.varabyte.kobweb.compose.css.WhiteSpace.NoWrap)
                                    .padding(topBottom = if (compact) 4.rpx else 6.rpx, leftRight = if (compact) 8.rpx else 12.rpx)
                                    .borderRadius(100.rpx)
                                    .styleModifier {
                                        property("background-color", badgeBg)
                                        property("color", badgeFg)
                                        property("text-transform", "uppercase")
                                    }
                            )
                        }
                        SpanText(
                            "${item.percent}%",
                            Modifier.fontSize(18.rpx).fontWeight(FontWeight.Bold)
                                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                        )
                    }
                    Box(
                        Modifier
                            .fillMaxWidth()
                            .height(6.rpx)
                            .borderRadius(3.rpx)
                            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF1, 0xF1, 0xFF))
                    ) {
                        Box(
                            Modifier
                                .fillMaxHeight()
                                .width(item.percent.percent)
                                .borderRadius(3.rpx)
                                .styleModifier {
                                    property("background", "linear-gradient(90deg, #CCF35F 1%, #22A529 97%)")
                                }
                        )
                    }
                }
            }
        }
    }
}

data class ProfileChip(val icon: String, val label: String)

@Composable
fun ProfileChips(chips: List<ProfileChip>, modifier: Modifier = Modifier, compact: Boolean = false) {
    val ink = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x01, 0x02, 0x26)
    Box(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(Colors.White)
            .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE7, 0xE7, 0xED))
            .borderRadius(20.rpx)
            .padding(topBottom = 24.rpx, leftRight = 20.rpx)
            .styleModifier { property("box-shadow", AppColors.Funnel.CardShadow) }
    ) {
        // compact (mobile): header row on one line, chips wrap below;
        // desktop: everything on one row, chips pushed to the right
        val header: @Composable () -> Unit = {
            SpanText(
                FunnelCopy.REPORT_PROFILE_TITLE,
                Modifier.fontSize(if (compact) 14.rpx else 18.rpx).lineHeight(20.rpx)
                    .fontWeight(FontWeight.SemiBold).color(ink)
                    .whiteSpace(com.varabyte.kobweb.compose.css.WhiteSpace.NoWrap)
                    .styleModifier { property("text-transform", "uppercase") }
            )
        }
        val redSub: @Composable () -> Unit = {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(6.rpx)) {
                org.jetbrains.compose.web.dom.Img(
                    src = "/funnel/sparkles-icon-red.svg",
                    attrs = { attr("aria-hidden", "true"); style { width(20.rpx); height(20.rpx) } }
                )
                SpanText(
                    FunnelCopy.REPORT_PROFILE_SUB,
                    Modifier.fontSize(14.rpx).fontWeight(FontWeight.Medium)
                        .whiteSpace(com.varabyte.kobweb.compose.css.WhiteSpace.NoWrap)
                        .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xBA, 0x2B, 0x2B))
                )
            }
        }
        Column(Modifier.fillMaxWidth().gap(16.rpx)) {
            if (compact) {
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    header()
                    redSub()
                }
            }
        Row(
            Modifier.fillMaxWidth().gap(16.rpx).flexWrap(FlexWrap.Wrap),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (!compact) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(16.rpx)) {
                    header()
                    redSub()
                }
            }
            Row(
                Modifier.gap(if (compact) 8.rpx else 12.rpx).flexWrap(FlexWrap.Wrap),
                verticalAlignment = Alignment.CenterVertically
            ) {
                chips.forEachIndexed { index, chip ->
                    if (!compact && index > 0) {
                        Box(
                            Modifier.size(6.rpx).borderRadius(50.percent)
                                .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xCC, 0xCC, 0xDE))
                        )
                    }
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier.gap(6.rpx).then(
                            if (compact) Modifier
                                .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xE7, 0xE7, 0xED))
                                .borderRadius(100.rpx)
                                .padding(topBottom = 8.rpx, leftRight = 12.rpx)
                            else Modifier
                        )
                    ) {
                        org.jetbrains.compose.web.dom.Img(
                            src = chip.icon,
                            attrs = { attr("aria-hidden", "true"); style { width(18.rpx); height(18.rpx) } }
                        )
                        SpanText(
                            chip.label,
                            Modifier.fontSize(if (compact) 14.rpx else 16.rpx).lineHeight(18.rpx)
                                .fontWeight(FontWeight.Medium).color(ink)
                        )
                    }
                }
            }
        }
        }
    }
}

/** Countdown that survives refresh via a stored deadline (epoch millis). */
@Composable
fun rememberCountdown(deadlineMillis: Double): State<Int> {
    val remaining = remember { mutableStateOf(secondsLeft(deadlineMillis)) }
    LaunchedEffect(deadlineMillis) {
        while (isActive) {
            remaining.value = secondsLeft(deadlineMillis)
            if (remaining.value <= 0) break
            delay(1000)
        }
    }
    return remaining
}

private fun secondsLeft(deadline: Double): Int {
    val diff = ((deadline - kotlin.js.Date.now()) / 1000).toInt()
    return if (diff < 0) 0 else diff
}

@Composable
fun TimerBanner(secondsRemaining: Int, modifier: Modifier = Modifier) {
    val minutes = secondsRemaining / 60
    val seconds = secondsRemaining % 60
    val mm = if (minutes < 10) "0$minutes" else "$minutes"
    val ss = if (seconds < 10) "0$seconds" else "$seconds"
    Row(
        modifier = modifier
            .width(400.rpx)
            .maxWidth(100.percent)
            .borderRadius(16.rpx)
            .padding(12.rpx)
            .styleModifier {
                property("background", "linear-gradient(152deg, #EB4757 1%, #CC2828 97%)")
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.gap(6.rpx)) {
            SpanText(
                FunnelCopy.TIMER_TITLE,
                Modifier.fontSize(10.rpx).lineHeight(12.rpx).color(Colors.White.withAlpha(0.9f))
            )
            Row(
                Modifier
                    .padding(topBottom = 2.rpx, leftRight = 6.rpx)
                    .borderRadius(100.rpx)
                    .backgroundColor(Colors.White.withAlpha(0.2f))
                    .border(1.rpx, LineStyle.Solid, Colors.White.withAlpha(0.3f))
                    .gap(2.rpx),
                verticalAlignment = Alignment.CenterVertically
            ) {
                org.jetbrains.compose.web.dom.Img(
                    src = "/funnel/discount-icon.svg",
                    attrs = { attr("aria-hidden", "true"); style { width(12.rpx); height(12.rpx) } }
                )
                SpanText(
                    FunnelCopy.TIMER_BADGE,
                    Modifier.fontSize(12.rpx).fontWeight(FontWeight.SemiBold).color(Colors.White)
                        .styleModifier { property("text-transform", "uppercase") }
                )
            }
        }
        SpanText("$mm:$ss", Modifier.fontSize(20.rpx).lineHeight(24.rpx).fontWeight(FontWeight.Bold).color(Colors.White))
    }
}

/**
 * A paid-trial package. [plan.amountCents] is the upfront charge today; after
 * [trialDays] the subscription renews into the main plan at [FunnelCopy.RENEWAL_CENTS]/month.
 * The struck-through original price and the weekly rate are fixed marketing
 * numbers per package — they don't divide exactly out of the plan price, so
 * they are passed in as overrides rather than derived.
 */
data class PlanDisplay(
    val plan: FunnelPlan,
    val trialDays: Int,
    val discountPct: Int,
    /** Struck-through package price; derived from [discountPct] when absent. */
    val originalCentsOverride: Long? = null,
    /** Advertised weekly rate; derived from the package price when absent. */
    val perWeekCentsOverride: Long? = null,
    val popular: Boolean = false,
) {
    val weeks: Int get() = (trialDays / 7).coerceAtLeast(1)
    val originalCents: Long get() = originalCentsOverride ?: ((plan.amountCents * 100) / (100 - discountPct))
    val perWeekCents: Long get() = perWeekCentsOverride ?: ((plan.amountCents + weeks / 2) / weeks)
    // Localized, e.g. "2 weeks plan" / "1 month plan" / "1 year plan".
    val label: String get() = FunnelCopy.planLabel(trialDays)
}

@Composable
fun PlanSelector(
    plans: List<PlanDisplay>,
    selectedUuid: String?,
    onSelect: (PlanDisplay) -> Unit,
    modifier: Modifier = Modifier,
    /** Mobile paywall: tighter rows so all three plans + CTA fit one phone screen. */
    compact: Boolean = false
) {
    val gray = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x82, 0x82, 0x93)
    val ink = com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x01, 0x02, 0x26)
    val struck = Modifier.styleModifier {
        property("text-decoration", "line-through")
        property("text-decoration-color", "#D70B0B")
    }

    Column(modifier = modifier.fillMaxWidth().gap(8.rpx).attrsModifier { attr("role", "radiogroup") }) {
        plans.forEach { display ->
            val selected = display.plan.uuid == selectedUuid
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(topBottom = if (compact) 14.rpx else 24.rpx, leftRight = if (compact) 14.rpx else 20.rpx)
                    .borderRadius(if (compact) 18.rpx else 24.rpx)
                    .cursor(Cursor.Pointer)
                    .backgroundColor(
                        if (selected) com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xF6, 0xFB, 0xFF)
                        else com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xFA, 0xFA, 0xFF)
                    )
                    .border(
                        1.rpx,
                        LineStyle.Solid,
                        if (selected) com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x14, 0x91, 0xFF)
                        else com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xD2, 0xD2, 0xDF)
                    )
                    .styleModifier { property("box-shadow", "4px 4px 8px rgba(0,0,0,0.05)") }
                    .onClick { onSelect(display) }
                    .attrsModifier {
                        attr("role", "radio")
                        attr("aria-checked", selected.toString())
                        attr("tabindex", "0")
                        onKeyDown {
                            if (it.key == "Enter" || it.key == " ") {
                                it.preventDefault()
                                onSelect(display)
                            }
                        }
                    },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(if (compact) 12.rpx else 16.rpx)) {
                    org.jetbrains.compose.web.dom.Img(
                        src = if (selected) "/funnel/radio-selected.svg" else "/funnel/radio-unselected.svg",
                        attrs = { attr("aria-hidden", "true"); style { width(24.rpx); height(24.rpx) } }
                    )
                    Column(Modifier.gap(if (compact) 4.rpx else 6.rpx)) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.gap(8.rpx).flexWrap(FlexWrap.Wrap)
                        ) {
                            SpanText(
                                display.label,
                                Modifier.fontSize(if (compact) 17.rpx else 20.rpx)
                                    .lineHeight(if (compact) 20.rpx else 24.rpx)
                                    .fontWeight(FontWeight.Bold).color(ink)
                            )
                            SpanText(
                                "-${display.discountPct}%",
                                Modifier
                                    .fontSize(if (compact) 12.rpx else 14.rpx)
                                    .fontWeight(FontWeight.Medium)
                                    .color(Colors.White)
                                    .padding(topBottom = if (compact) 2.rpx else 4.rpx, leftRight = if (compact) 6.rpx else 8.rpx)
                                    .borderRadius(100.rpx)
                                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xD5, 0x30, 0x30))
                            )
                            if (display.popular) SpanText(
                                FunnelCopy.MOST_POPULAR,
                                Modifier
                                    .fontSize(if (compact) 11.rpx else 13.rpx)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(Colors.White)
                                    .padding(topBottom = if (compact) 2.rpx else 4.rpx, leftRight = if (compact) 6.rpx else 8.rpx)
                                    .borderRadius(100.rpx)
                                    .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x14, 0x91, 0xFF))
                            )
                        }
                        Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(6.rpx)) {
                            SpanText(
                                formatUsdCents(display.originalCents),
                                Modifier.fontSize(if (compact) 14.rpx else 18.rpx).color(gray).then(struck)
                            )
                            SpanText(
                                formatUsdCents(display.plan.amountCents),
                                Modifier.fontSize(if (compact) 14.rpx else 18.rpx).color(gray)
                            )
                        }
                    }
                }
                Column(horizontalAlignment = Alignment.End, modifier = Modifier.gap(2.rpx)) {
                    SpanText(
                        formatUsdCents(display.perWeekCents),
                        Modifier.fontSize(if (compact) 22.rpx else 28.rpx)
                            .lineHeight(if (compact) 26.rpx else 32.rpx)
                            .fontWeight(FontWeight.Bold)
                            .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
                    )
                    SpanText(FunnelCopy.PER_WEEK, Modifier.fontSize(if (compact) 12.rpx else 14.rpx).color(gray))
                }
            }
        }
    }
}

/**
 * Prominent money-back plaque for the paywall: gold seal + bold guarantee
 * headline + reassurance sub-line, framed in a gold-tinted card.
 */
@Composable
fun GuaranteeBadge(modifier: Modifier = Modifier) {
    Row(
        modifier = modifier
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xFF, 0xFB, 0xEF))
            .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xEE, 0xDC, 0xA8))
            .borderRadius(18.rpx)
            .padding(topBottom = 12.rpx, leftRight = 14.rpx)
            .gap(12.rpx)
            .styleModifier { property("box-shadow", "4px 4px 8px rgba(0,0,0,0.05)") },
        verticalAlignment = Alignment.CenterVertically
    ) {
        org.jetbrains.compose.web.dom.Img(
            src = "/funnel/money-back-seal.png",
            attrs = {
                attr("alt", "")
                style { width(72.rpx); height(54.rpx); flexShrink(0) }
            }
        )
        Column(Modifier.gap(4.rpx)) {
            SpanText(
                FunnelCopy.GUARANTEE,
                Modifier.fontSize(16.rpx).lineHeight(20.rpx).fontWeight(FontWeight.Bold)
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x31, 0x32, 0x3F))
            )
            SpanText(
                FunnelCopy.GUARANTEE_SUB,
                Modifier.fontSize(13.rpx).lineHeight(17.rpx)
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x6B, 0x6B, 0x6B))
            )
        }
    }
}

@Composable
fun GuaranteeLine(modifier: Modifier = Modifier, emphasized: Boolean = false) {
    val iconSize = if (emphasized) 26 else 20
    Row(
        modifier = modifier.gap(if (emphasized) 8.rpx else 4.rpx),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.Center
    ) {
        org.jetbrains.compose.web.dom.Img(
            src = "/funnel/shield-check.svg",
            attrs = { attr("aria-hidden", "true"); style { width(iconSize.rpx); height(iconSize.rpx) } }
        )
        SpanText(
            FunnelCopy.GUARANTEE,
            Modifier
                .fontSize(if (emphasized) 20.rpx else 16.rpx)
                .lineHeight(if (emphasized) 24.rpx else 18.rpx)
                .fontWeight(if (emphasized) FontWeight.Bold else FontWeight.Normal)
                .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x31, 0x32, 0x3F))
        )
    }
}
